import clsx from 'clsx';
import type { JSX } from 'react';
import { FormField } from '@/components/form';
import type { FloorLevelCode } from '@/lib/types/property';
import { FLOOR_LEVEL_OPTIONS, type UnitFormErrors, type UnitFormState } from './unitFormState';

interface UnitIdentitySectionProps {
  formState: UnitFormState;
  errors: UnitFormErrors;
  disabled: boolean;
  isUploadingHero: boolean;
  setFormState: React.Dispatch<React.SetStateAction<UnitFormState>>;
  clearFieldError: (field: keyof UnitFormState) => void;
}

const inputClass =
  'rounded-2xl border border-surface-subtle px-4 py-2.5 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-60 md:py-2 md:text-sm';

export function UnitIdentitySection({
  formState,
  errors,
  disabled,
  isUploadingHero,
  setFormState,
  clearFieldError,
}: UnitIdentitySectionProps): JSX.Element {
  return (
    <>
      <section className="grid gap-4 md:grid-cols-2">
        <FormField
          id="unit-label"
          label="Unit label"
          description="Shown on public unit cards and detail pages. Example: 302B."
          error={errors.unitLabel}
          required
        >
          <input
            type="text"
            value={formState.unitLabel}
            onChange={(event) => {
              setFormState((prev) => ({ ...prev, unitLabel: event.target.value }));
              clearFieldError('unitLabel');
            }}
            maxLength={64}
            disabled={disabled || isUploadingHero}
            className={inputClass}
          />
        </FormField>

        <FormField
          id="unit-floor"
          label="Floor"
          description="Optional. Shown as the floor on public unit details when set."
          error={errors.floorLevel}
        >
          <select
            value={formState.floorLevel}
            onChange={(event) => {
              setFormState((prev) => ({
                ...prev,
                floorLevel: event.target.value as FloorLevelCode,
              }));
              clearFieldError('floorLevel');
            }}
            disabled={disabled || isUploadingHero}
            className={clsx(inputClass, 'bg-white')}
          >
            <option value="">No floor selected</option>
            {FLOOR_LEVEL_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </FormField>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <FormField
          id="unit-area"
          label="Area (sf)"
          description="Displayed as the unit size on public listings and detail pages. Example: 3500."
          error={errors.areaSf}
          required
        >
          <input
            type="number"
            inputMode="decimal"
            min={1}
            value={formState.areaSf}
            onChange={(event) => {
              setFormState((prev) => ({ ...prev, areaSf: event.target.value }));
              clearFieldError('areaSf');
            }}
            disabled={disabled || isUploadingHero}
            className={inputClass}
          />
        </FormField>

        <FormField
          id="unit-divisible"
          label="Minimum divisible sf"
          description="If set, public listings show a size range or a Min divisible note. Example: 1200."
          error={errors.minDivisibleSf}
        >
          <input
            type="number"
            inputMode="decimal"
            min={0}
            value={formState.minDivisibleSf}
            onChange={(event) => {
              setFormState((prev) => ({ ...prev, minDivisibleSf: event.target.value }));
              clearFieldError('minDivisibleSf');
            }}
            disabled={disabled || isUploadingHero}
            className={inputClass}
          />
        </FormField>
      </section>
    </>
  );
}
